import { Availabilities } from 'api/menuMapping/types'
import MinusIcon from 'assets/icons/outline_minus_button.svg'
import PlusIcon from 'assets/icons/outline_plus_button.svg'
import { Button } from 'components'
import CustomTimePicker from 'components/CustomTimePicker/CustomTimePicker'
import { useIsMobile } from 'hooks'
import moment, { Moment } from 'moment'
import { Fragment, useEffect, useRef, useState } from 'react'
import { FormattedAvailabilities } from 'types/Menu'
import { DAYS_OF_WEEK } from 'utils'
import { convertMomentToStringTimePeriod, convertTimePeriodToMoment } from 'utils/DateTime'

import {
  Checkbox,
  CheckboxWrapper,
  DayClosedRow,
  IconButton,
  LineSeparator,
  MenuAvailabilityDialogActions,
  MenuAvailabilityErrorText,
  MenuAvailabilityTableCtn,
  MenuAvailabilityTableDayContainer,
  MenuAvailabilityTableErrorText,
  MenuAvailabilityTableIconButtonContainer,
  MenuAvailabilityTableLineSeparatorContainer,
  MenuAvailabilityTableRowContainer,
  MenuAvailabilityTableSeparatorContainer,
  MenuAvailabilityTableTimePickerContainer,
} from './../styles'

type Props = {
  availabilities: Availabilities[]
  newAvailabilities: FormattedAvailabilities
  setNewAvailabilities: (newData: FormattedAvailabilities) => void
  availabilitiesToDelete?: Availabilities['id'][]
  setAvailabilitiesToDelete?: (ids: Availabilities['id'][]) => void
  timePeriodsError: boolean
  saveButtonDisabled: boolean
  onSave: () => void
  onCancel: () => void
}

const isError = (startValue?: string, endValue?: string) => {
  if ((!startValue && endValue) || (startValue && !endValue)) {
    return true
  }
  if (startValue && endValue) {
    return startValue >= endValue
  }
  return false
}

const TIME_TYPE = {
  START: 'start',
  END: 'end',
}

const MenuHoursForm = ({
  newAvailabilities,
  setNewAvailabilities,
  availabilitiesToDelete,
  setAvailabilitiesToDelete,
  timePeriodsError,
  saveButtonDisabled,
  onSave,
  onCancel,
}: Props) => {
  const [closedDays, setClosedDays] = useState<Record<string, boolean>>({})
  const { isMobile } = useIsMobile()
  const timePickerRefs = useRef<(HTMLInputElement | null)[]>([])
  const [showErrorText, setShowErrorText] = useState(false)

  // Initialize refs array with the correct size
  useEffect(() => {
    const totalTimePickers = DAYS_OF_WEEK.reduce((acc, day) => {
      const periods = newAvailabilities[day] || []
      return acc + periods.length * 2 // Each period has 2 time pickers
    }, 0)
    timePickerRefs.current = Array(totalTimePickers).fill(null)
  }, [newAvailabilities])

  const handleTimeSelected = (dayIndex: number, timePeriodIdx = 0) => {
    // Calculate the correct index for the next time picker
    const currentIndex = dayIndex * 2 + timePeriodIdx * 2
    const nextTimePickerIndex = currentIndex + 1

    // Use setTimeout to ensure the focus happens after the current event cycle
    setTimeout(() => {
      // Only focus if the next time picker exists
      if (timePickerRefs.current[nextTimePickerIndex]) {
        timePickerRefs.current[nextTimePickerIndex]?.focus()
        // Show error text after focusing the "Closes at" picker
        setShowErrorText(true)
      }
    }, 0)
  }

  const handleChangeTime = async (
    newValue: Moment,
    day: string,
    timePeriodIdx: number,
    type: string,
  ) => {
    const modifiedAvailabilityObj = { ...newAvailabilities }

    if (!modifiedAvailabilityObj[day]) {
      modifiedAvailabilityObj[day] = []
    }

    const currentTimePeriodValues = modifiedAvailabilityObj[day]?.[timePeriodIdx]

    // clear both start and end times for the period
    if (currentTimePeriodValues && !newValue) {
      currentTimePeriodValues.start_time = undefined
      currentTimePeriodValues.end_time = undefined

      // if this exists in the db, stage this time period id for deletion
      if (currentTimePeriodValues.id && availabilitiesToDelete && setAvailabilitiesToDelete) {
        setAvailabilitiesToDelete([
          ...new Set([...availabilitiesToDelete, currentTimePeriodValues.id]),
        ])
      }
    }

    const convertedTimeStr = convertMomentToStringTimePeriod(newValue)

    if (type === TIME_TYPE.START) {
      // update current value
      if (currentTimePeriodValues) {
        currentTimePeriodValues.start_time = convertedTimeStr
      } else {
        // add new time period for the day
        modifiedAvailabilityObj[day][timePeriodIdx] = {
          start_time: convertedTimeStr,
        }
      }
    }
    if (type === TIME_TYPE.END) {
      // update current value
      if (currentTimePeriodValues) {
        currentTimePeriodValues.end_time = convertedTimeStr
      } else {
        // add new time period for the day
        modifiedAvailabilityObj[day][timePeriodIdx] = {
          end_time: convertedTimeStr,
        }
      }
    }

    setNewAvailabilities(modifiedAvailabilityObj)
  }

  const addNewTimePeriodRow = (dayOfWeek: string) => {
    const updatedAvailabilities = { ...newAvailabilities }

    const currTimePeriods = updatedAvailabilities[dayOfWeek]
    // create empty time periods
    currTimePeriods.push({
      start_time: undefined,
      end_time: undefined,
    })

    updatedAvailabilities[dayOfWeek] = currTimePeriods
    setNewAvailabilities(updatedAvailabilities)
  }

  const removeTimePeriodRow = async (dayOfWeek: string, timePeriodIndex: number) => {
    if (timePeriodIndex === 0) {
      console.error('Cannot remove the first time period')
      return
    }

    const updatedAvailabilities = { ...newAvailabilities }
    const currTimePeriods = updatedAvailabilities[dayOfWeek]
    const existingTimePeriodId = currTimePeriods[timePeriodIndex]?.id

    if (existingTimePeriodId && availabilitiesToDelete && setAvailabilitiesToDelete) {
      setAvailabilitiesToDelete([...new Set([...availabilitiesToDelete, existingTimePeriodId])])
    }

    currTimePeriods.splice(timePeriodIndex, 1)
    updatedAvailabilities[dayOfWeek] = currTimePeriods
    setNewAvailabilities(updatedAvailabilities)
  }

  const handleToggleClosedDay = (day: string) => {
    const updatedClosedDays = {
      ...closedDays,
      [day]: !closedDays[day],
    }

    if (updatedClosedDays[day]) {
      const updatedAvailabilities = { ...newAvailabilities }
      updatedAvailabilities[day] = []
      setNewAvailabilities(updatedAvailabilities)
    }

    setClosedDays(updatedClosedDays)
  }

  const handleSave = () => {
    if (timePeriodsError) return
    onSave()
  }

  useEffect(() => {
    const initClosedDays: Record<string, boolean> = {}

    DAYS_OF_WEEK.forEach((day) => {
      const dayPeriods = newAvailabilities[day]
      initClosedDays[day] = !dayPeriods || dayPeriods.length === 0
    })

    setClosedDays(initClosedDays)
  }, [newAvailabilities])

  return (
    <>
      <MenuAvailabilityTableCtn isModal={true}>
        {DAYS_OF_WEEK.map((day, dayIndex) => {
          const firstTimePeriod = newAvailabilities[day]?.[0]
          const otherTimePeriods = newAvailabilities[day]?.slice(1)

          const shortDay = moment(day, 'dddd').format('ddd')

          return (
            <Fragment key={day}>
              <MenuAvailabilityTableRowContainer key={day}>
                <MenuAvailabilityTableDayContainer>
                  {isMobile ? shortDay : day}
                  <DayClosedRow>
                    <CheckboxWrapper>
                      <Checkbox
                        checked={closedDays[day] || false}
                        onClick={() => handleToggleClosedDay(day)}
                        readOnly
                      />
                    </CheckboxWrapper>
                    <span>Closed</span>
                  </DayClosedRow>
                </MenuAvailabilityTableDayContainer>
                {!closedDays[day] && (
                  <MenuAvailabilityTableTimePickerContainer>
                    <CustomTimePicker
                      key={`${day}-1`}
                      label='Opens at'
                      defaultValue={convertTimePeriodToMoment(firstTimePeriod?.start_time)?.format(
                        'h:mm A',
                      )}
                      onChange={(newValue) =>
                        handleChangeTime(moment(newValue, 'h:mm A'), day, 0, TIME_TYPE.START)
                      }
                      ref={(el) => {
                        timePickerRefs.current[dayIndex * 2] = el
                      }}
                      isFirstTimePicker={true}
                      onTimeSelected={() => handleTimeSelected(dayIndex, 0)}
                    />
                    <MenuAvailabilityTableSeparatorContainer>
                      <LineSeparator />
                    </MenuAvailabilityTableSeparatorContainer>
                    <CustomTimePicker
                      key={`${day}-2`}
                      label='Closes at'
                      defaultValue={convertTimePeriodToMoment(firstTimePeriod?.end_time)?.format(
                        'h:mm A',
                      )}
                      onChange={(newValue) =>
                        handleChangeTime(moment(newValue, 'h:mm A'), day, 0, TIME_TYPE.END)
                      }
                      ref={(el) => {
                        timePickerRefs.current[dayIndex * 2 + 1] = el
                      }}
                    />
                  </MenuAvailabilityTableTimePickerContainer>
                )}
                {!closedDays[day] && (
                  <IconButton type='button' onClick={() => addNewTimePeriodRow(day)}>
                    <img src={PlusIcon} alt='expand_rows' />
                  </IconButton>
                )}
              </MenuAvailabilityTableRowContainer>
              {isError(firstTimePeriod?.start_time, firstTimePeriod?.end_time) && (
                <MenuAvailabilityTableErrorText>
                  Error: the end time has to be greater than the start time.
                </MenuAvailabilityTableErrorText>
              )}

              {otherTimePeriods?.map((timePeriod, idx) => {
                const timePeriodIdx = idx + 1
                const baseIndex = dayIndex * 2 + timePeriodIdx * 2

                return (
                  <Fragment key={`${day}-${timePeriodIdx}`}>
                    <MenuAvailabilityTableRowContainer>
                      <MenuAvailabilityTableDayContainer />
                      <MenuAvailabilityTableTimePickerContainer>
                        <CustomTimePicker
                          key={`${day}-${timePeriodIdx}-1`}
                          label='Opens at'
                          defaultValue={convertTimePeriodToMoment(timePeriod?.start_time)?.format(
                            'h:mm A',
                          )}
                          onChange={(newValue) =>
                            handleChangeTime(
                              moment(newValue, 'h:mm A'),
                              day,
                              timePeriodIdx,
                              TIME_TYPE.START,
                            )
                          }
                          ref={(el) => {
                            timePickerRefs.current[baseIndex] = el
                          }}
                          isFirstTimePicker={true}
                          onTimeSelected={() => handleTimeSelected(dayIndex, timePeriodIdx)}
                        />
                        <MenuAvailabilityTableSeparatorContainer>
                          <LineSeparator />
                        </MenuAvailabilityTableSeparatorContainer>
                        <CustomTimePicker
                          key={`${day}-${timePeriodIdx}-2`}
                          label='Closes at'
                          defaultValue={convertTimePeriodToMoment(timePeriod?.end_time)?.format(
                            'h:mm A',
                          )}
                          onChange={(newValue) =>
                            handleChangeTime(
                              moment(newValue, 'h:mm A'),
                              day,
                              timePeriodIdx,
                              TIME_TYPE.END,
                            )
                          }
                          ref={(el) => {
                            timePickerRefs.current[baseIndex + 1] = el
                          }}
                        />
                      </MenuAvailabilityTableTimePickerContainer>
                      <MenuAvailabilityTableIconButtonContainer>
                        <IconButton
                          type='button'
                          onClick={() => removeTimePeriodRow(day, timePeriodIdx)}
                        >
                          <img src={MinusIcon} alt='remove_row' />
                        </IconButton>
                      </MenuAvailabilityTableIconButtonContainer>
                    </MenuAvailabilityTableRowContainer>
                    {isError(timePeriod?.start_time, timePeriod?.end_time) && (
                      <MenuAvailabilityTableErrorText>
                        Error: the end time has to be greater than the start time.
                      </MenuAvailabilityTableErrorText>
                    )}
                    {timePeriodIdx === otherTimePeriods.length && (
                      <MenuAvailabilityTableLineSeparatorContainer>
                        <LineSeparator />
                      </MenuAvailabilityTableLineSeparatorContainer>
                    )}
                  </Fragment>
                )
              })}
            </Fragment>
          )
        })}
      </MenuAvailabilityTableCtn>
      <MenuAvailabilityDialogActions>
        {timePeriodsError && showErrorText && (
          <MenuAvailabilityErrorText>
            Please fix the errors above before proceeding.
          </MenuAvailabilityErrorText>
        )}
        <Button onClick={onCancel} size='small' variant='textonly' narrow>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={timePeriodsError || saveButtonDisabled}
          size='small'
          narrow
        >
          Save
        </Button>
      </MenuAvailabilityDialogActions>
    </>
  )
}

export default MenuHoursForm
